import { MeetingResponse } from 'api/meedioBackend/types';

import { wasMeetingStartedEarly } from './wasMeetingStartedEarly';

/**
 * Returns true when a scheduled meeting has ended and should no longer be editable.
 *
 * Three signals are checked:
 *   1. `ended_at !== null` — backend explicitly marked the meeting as ended (cron sweep or
 *      last-participant-leave event during the scheduled window).
 *   2. `started_at !== null` AND the meeting is outside its scheduled window — covers the
 *      early-departure case where the host starts before `scheduled_at`, leaves, and the
 *      backend has not yet set `ended_at` (cron fires later). The meeting is not "ongoing"
 *      per classifyMeetings in this state (window not open / already closed), so blocking
 *      edit is correct.
 *   3. `wasMeetingStartedEarly` — the meeting was started before `scheduled_at`. Once the
 *      window opens (`scheduled_at <= now <= ends_at`) the original schedule is already in
 *      the past relative to the early run, so the meeting must not become editable again.
 *      Meetings started **on time or late** and currently inside their window remain editable
 *      to preserve the existing ongoing-meeting edit path.
 *
 * Cancelled meetings are excluded — already non-editable via the existing cancel gate.
 * Instant (non-scheduled) rooms are excluded — `scheduled_at === null`.
 */
export const isEndedScheduledMeeting = (meeting: MeetingResponse): boolean => {
  if (meeting.scheduled_at === null) return false;
  if (meeting.cancelled_at !== null) return false;
  if (meeting.ended_at !== null) return true;

  if (meeting.started_at !== null) {
    const now = Date.now();
    const scheduledAt = new Date(meeting.scheduled_at).getTime();
    // Started early and departed before the window opened
    if (scheduledAt > now) return true;
    // Window has fully closed (cron lag: ends_at passed but ended_at not yet written)
    if (meeting.ends_at !== null && new Date(meeting.ends_at).getTime() <= now) return true;
    // Started before its scheduled time and the window has now opened — the meeting already
    // ran and is over; once scheduled_at arrives it must not become editable again.
    if (wasMeetingStartedEarly(meeting)) return true;
  }

  return false;
};
